import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaInstagram, FaLinkedin } from 'react-icons/fa';
import axios from 'axios';
import {
  LuCalendarDays,
  LuMapPin,
  LuChevronDown,
  LuArrowRight,
  LuArrowLeft,
  LuCheck,
} from 'react-icons/lu';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';

const API = process.env.REACT_APP_API_URL || '';

// Shared visual language for the landing, program information, and application.
const CREATOR_FLOW_THEME = {
  font: 'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  ink: '#171717',
  muted: '#62625d',
  border: 'rgba(21,21,21,.12)',
  background: 'radial-gradient(ellipse at 50% 44%, rgba(246,246,244,.92) 0%, rgba(251,251,250,.68) 30%, rgba(255,255,255,0) 62%), #fff',
};

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  birthday: '',
  livesInLosAngeles: '',
  instagramHandle: '',
  tiktokHandle: '',
  laNetworkSize: '',
  outreachComfort: '',
  coldDmWillingness: '',
  typicalVideoViews: '',
};

const STEPS = [
  { id: 'name', number: '01', title: "What's your name?", type: 'name' },
  { id: 'email', number: '02', title: "What's your email?", type: 'email' },
  { id: 'birthday', number: '03', title: "What's your birthday?", description: 'You must be 18 or older to apply.', type: 'birthday' },
  { id: 'livesInLosAngeles', number: '04', title: 'Do you live in the Los Angeles area?', type: 'choice', options: ['Yes', 'No'] },
  { id: 'instagramHandle', number: '05', title: "What's your Instagram handle?", description: 'Your account must be public.', type: 'text', placeholder: '@username' },
  { id: 'tiktokHandle', number: '06', title: "What's your TikTok handle?", description: 'Optional. If you have a TikTok account, you’ll also post your videos there.', type: 'text', placeholder: '@username', optional: true },
  { id: 'laNetworkSize', number: '07', title: 'About how many people in LA could you personally invite to Tribe?', type: 'choice', options: ['Fewer than 10', '10–25', '26–50', '51–100', 'More than 100'] },
  { id: 'outreachComfort', number: '08', title: 'How comfortable are you personally messaging friends, followers, and acquaintances to invite them to Tribe?', type: 'choice', options: ['Very comfortable', 'Somewhat comfortable', 'Not comfortable'] },
  { id: 'coldDmWillingness', number: '09', title: 'Are you willing to message people you don’t know to invite them to Tribe?', type: 'choice', options: ['Yes', 'No'] },
  { id: 'typicalVideoViews', number: '10', title: 'About how many views do your recent videos typically get on your higher-performing platform?', type: 'choice', options: ['Under 500', '500–999', '1,000–2,499', '2,500–4,999', '5,000+', 'I haven’t posted videos yet'] },
];

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function calculateAge(birthday) {
  const match = String(birthday || '').match(
    /^(\d{2})\/(\d{2})\/(\d{4})$/
  );

  if (!match) return null;

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const birthDate = new Date(year, month - 1, day);

  if (
    birthDate.getFullYear() !== year ||
    birthDate.getMonth() !== month - 1 ||
    birthDate.getDate() !== day
  ) {
    return null;
  }

  const today = new Date();
  if (birthDate > today) return null;

  let age = today.getFullYear() - year;
  const birthdayHasPassed =
    today.getMonth() > month - 1 ||
    (today.getMonth() === month - 1 && today.getDate() >= day);

  if (!birthdayHasPassed) age -= 1;

  return age;
}

export default function ReferralPartnerApplication() {
  const [screen, setScreen] = useState('intro');
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [error, setError] = useState('');
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const inputRef = useRef(null);

  const step = STEPS[stepIndex];
  useEffect(() => {
    if (screen !== 'form') return;
    const timer = window.setTimeout(() => inputRef.current?.focus?.(), 80);
    return () => window.clearTimeout(timer);
  }, [screen, stepIndex]);

  const canContinue = useMemo(() => {
    if (!step) return false;
    if (step.optional) return true;

    switch (step.id) {
      case 'name':
        return Boolean(form.firstName.trim() && form.lastName.trim());
      case 'livesInLosAngeles':
        return form.livesInLosAngeles === 'Yes';
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
      case 'birthday': {
        const age = calculateAge(form.birthday);
        return Number.isFinite(age) && age >= 18;
      }
      default:
        return Boolean(String(form[step.id] || '').trim());
    }
  }, [form, step]);

  function updateField(field, value) {
    setError('');
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function showProgramInfo() {
    setScreen('info');
    setError('');
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  function showLanding() {
    setScreen('intro');
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  function startApplication() {
    setScreen('form');
    setStepIndex(0);
    setError('');
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  function previousStep() {
    if (submitting) return;
    setError('');
    if (stepIndex === 0) {
      showProgramInfo();
      return;
    }
    setStepIndex((prev) => prev - 1);
  }

  async function nextStep() {
    if (!canContinue || submitting || checkingEmail) return;
    setError('');

    if (step.id === 'email') {
      setCheckingEmail(true);

      try {
        const response = await axios.post(
          `${API}/referral-partners/check-email`,
          { email: normalizeEmail(form.email) }
        );

        if (response?.data?.exists) {
          setError('An application already exists with this email.');
          return;
        }
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            'Unable to verify this email right now. Please try again.'
        );
        return;
      } finally {
        setCheckingEmail(false);
      }
    }

    if (stepIndex < STEPS.length - 1) {
      setStepIndex((prev) => prev + 1);
      return;
    }

    setShowSubmitConfirm(true);
  }

  function closeSubmitConfirm() {
    if (submitting) return;
    setShowSubmitConfirm(false);
  }

  async function confirmSubmit() {
    if (submitting) return;
    setShowSubmitConfirm(false);
    await submitApplication();
  }

  async function submitApplication() {
    if (form.livesInLosAngeles !== 'Yes') {
      setError('Our creator program is currently only open to people who live in the Los Angeles area.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
await axios.post(`${API}/referral-partners`, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: normalizeEmail(form.email),
        age: calculateAge(form.birthday),
        city: 'Los Angeles area',
        livesInLosAngeles: form.livesInLosAngeles === 'Yes',
        instagramHandle: form.instagramHandle.trim()
          ? `@${form.instagramHandle.trim().replace(/^@+/, '')}`
          : '',
        tiktokHandle: form.tiktokHandle.trim()
          ? `@${form.tiktokHandle.trim().replace(/^@+/, '')}`
          : '',
        laNetworkSize: form.laNetworkSize,
        outreachComfort: form.outreachComfort,
        coldDmWillingness: form.coldDmWillingness === 'Yes',
        typicalVideoViews: form.typicalVideoViews,
      });

      setScreen('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Something went wrong. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  function selectChoice(value) {
    updateField(step.id, value);
  }

  function handleKeyDown(event) {
    if (showSubmitConfirm && event.key === 'Escape') {
      event.preventDefault();
      closeSubmitConfirm();
      return;
    }

    if (showSubmitConfirm) return;

    if (screen === 'intro' && event.key === 'Enter') {
      if (event.target?.closest?.('button, a')) return;
      event.preventDefault();
      showProgramInfo();
      return;
    }

    if (screen !== 'form' || event.key !== 'Enter') return;

    if (event.target?.tagName === 'TEXTAREA') {
      if (!event.metaKey && !event.ctrlKey) return;
    }

    event.preventDefault();
    nextStep();
  }

  if (screen === 'intro') {
    return (
      <main
        className="referralApplicationLanding"
        style={styles.landingPage}
        onKeyDown={handleKeyDown}
      >
        <ResponsiveStyles />

        <header
          className="referralLandingHeader"
          style={styles.landingHeader}
        >
          <TribeLogo
            className="referralLogoBlack"
            style={styles.landingLogo}
            aria-label="Tribe"
          />

          <div style={styles.landingHeaderTitle}>
            CREATOR APPLICATION
          </div>
        </header>

        <div
          className="referralLandingAmbient"
          style={styles.landingAmbient}
          aria-hidden="true"
        >
          <div
            className="referralAmbient referralAmbientProspect"
            style={styles.ambientProspect}
          >
            <span style={styles.ambientAvatar}>M</span>

            <div style={styles.ambientTextStack}>
              <strong>Strong prospect</strong>
              <small>Maya, 26 · Los Angeles</small>
            </div>

            <span style={styles.ambientStatus}>FIT</span>
          </div>

          <div
            className="referralAmbient referralAmbientMessage"
            style={styles.ambientMessage}
          >
            <span style={styles.ambientDot} />

            <p>
              You seem cool! I wanted to offer you an invite.
            </p>
          </div>

          <div
            className="referralAmbient referralAmbientQualified"
            style={styles.ambientQualified}
          >
            <span style={styles.ambientCheck}>✓</span>

            <div style={styles.ambientTextStack}>
              <strong>New referral</strong>
              <small>Signup or application complete</small>
            </div>
          </div>

          <div
            className="referralAmbient referralAmbientEarnings"
            style={styles.ambientEarnings}
          >
            <span style={styles.ambientSmallLabel}>THIS MONTH</span>

            <div style={styles.ambientEarningsRow}>
              <strong>$400</strong>
              <i />
              <div>
                <b>50</b>
                <span>signups</span>
              </div>
            </div>
          </div>

          <div
            className="referralAmbient referralAmbientCode"
            style={styles.ambientCode}
          >
            <span style={styles.ambientSmallLabel}>YOUR REFERRAL CODE</span>
            <strong>YOURCODE</strong>
            <small>Copy</small>
          </div>
        </div>

        <section
          className="referralLandingContent"
          style={styles.landingContent}
        >
          <h1 style={styles.landingTitle}>
Help bring great <br/> members into Tribe.          </h1>

       <p style={styles.landingCopy}>
  We’re looking for friendly, outgoing people to help grow Tribe’s membership
  through simple videos, personal invitations, and cold outreach.
</p>
          <div style={styles.landingActions}>
            <button
              type="button"
              onClick={showProgramInfo}
              style={styles.landingButton}
            >
              View program
            </button>

            <span
              className="referralEnterHint"
              style={styles.landingEnterHint}
            >
              press <strong>Enter ↵</strong>
            </span>
          </div>
        </section>

        <ApplicationFooter />
      </main>
    );
  }

  if (screen === 'info') {
    return <CreatorProgramIntro onApply={startApplication} onBack={showLanding} />;
  }

  if (screen === 'success') {
    return (
      <main className="referralApplicationPage" style={styles.formPage}>
        <ResponsiveStyles />
        <Header minimal />

        <section style={styles.successShell}>
          <div style={styles.successMark}>✓</div>

          <h1 style={styles.successTitle}>
            Application received.
          </h1>

          <p style={styles.successCopy}>
            Thanks for applying. We’ll review your application and reach out by
            email if we’d like to schedule an onboarding call.
          </p>

          <a
            href="https://www.jointribe.io/events"
            style={styles.successButton}
          >
            Back to Tribe
          </a>
        </section>

        <ApplicationFooter />
      </main>
    );
  }

  return (
    <main className="referralApplicationPage" style={styles.formPage} onKeyDown={handleKeyDown}>
      <ResponsiveStyles />
      <Header />

      <div
        className="referralFormAmbient"
        style={styles.formAmbient}
        aria-hidden="true"
      >
        <div
          className="referralFormAmbientCard referralFormAmbientProspect"
          style={{
            ...styles.formAmbientCard,
            ...styles.formAmbientProspect,
          }}
        >
          <span style={styles.formAmbientAvatar}>M</span>

          <div style={styles.formAmbientCopy}>
            <strong>Strong prospect</strong>
            <small>Los Angeles · 20s</small>
          </div>
        </div>

        <div
          className="referralFormAmbientCard referralFormAmbientQualified"
          style={{
            ...styles.formAmbientCard,
            ...styles.formAmbientQualified,
          }}
        >
          <span style={styles.formAmbientCheck}>✓</span>

          <div style={styles.formAmbientCopy}>
            <strong>New referral</strong>
            <small>Signup or application complete</small>
          </div>
        </div>

        <div
          className="referralFormAmbientCard referralFormAmbientCode"
          style={{
            ...styles.formAmbientCard,
            ...styles.formAmbientCode,
          }}
        >
          <span style={styles.formAmbientLabel}>
            REFERRAL CODE
          </span>
          <strong>YOURCODE</strong>
        </div>
      </div>

      <section style={styles.questionShell}>
        <div
          className="referralQuestionContent"
          style={styles.questionContent}
        >
          <div style={styles.questionNumber}>
            {step.number} →
          </div>

          <h1 style={styles.questionTitle}>
            {step.title}
          </h1>

          {step.description ? (
            <p style={styles.questionDescription}>
              {step.description}
            </p>
          ) : null}

          <div style={styles.answerArea}>
            {renderAnswer({
              step,
              form,
              inputRef,
              updateField,
              selectChoice,
            })}
          </div>

          {step.id === 'livesInLosAngeles' && form.livesInLosAngeles === 'No' ? (
            <p role="status" style={styles.questionDescription}>
              Our creator program is currently only open to people who live in the Los Angeles area.
            </p>
          ) : null}

          {error ? <div role="alert" style={styles.errorBox}>{error}</div> : null}

          <div className="referralDesktopActions" style={styles.desktopActions}>
            <button
              type="button"
              onClick={previousStep}
              disabled={submitting}
              style={styles.backButton}
            >
              Back
            </button>

            <button
              type="button"
              onClick={nextStep}
              disabled={!canContinue || submitting || checkingEmail}
              style={{
                ...styles.continueButton,
                ...(!canContinue || submitting || checkingEmail
                  ? styles.continueButtonDisabled
                  : {}),
              }}
            >
              {submitting
                ? 'Submitting…'
                : stepIndex === STEPS.length - 1
                ? 'Submit application'
                : step.optional && !String(form[step.id] || '').trim()
                ? 'Skip'
                : 'Continue'}
            </button>
          </div>

        </div>
      </section>

      <div className="referralMobileControls" style={styles.bottomControls}>
        <div style={styles.bottomControlsInner}>
          <button
            type="button"
            onClick={previousStep}
            disabled={submitting}
            style={styles.backButton}
          >
            Back
          </button>

          <button
            type="button"
            onClick={nextStep}
            disabled={!canContinue || submitting || checkingEmail}
            style={{
              ...styles.continueButton,
              ...(!canContinue || submitting || checkingEmail
                ? styles.continueButtonDisabled
                : {}),
            }}
          >
            {submitting
              ? 'Submitting…'
              : stepIndex === STEPS.length - 1
              ? 'Submit application'
              : step.optional && !String(form[step.id] || '').trim()
              ? 'Skip'
              : 'Continue'}
          </button>
        </div>
      </div>

      {showSubmitConfirm ? (
        <div
          className="referralSubmitModalBackdrop"
          style={styles.submitModalBackdrop}
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeSubmitConfirm();
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="referral-submit-confirm-title"
            style={styles.submitModal}
          >
            <div style={styles.submitModalEyebrow}>
              SUBMIT APPLICATION
            </div>

            <h2
              id="referral-submit-confirm-title"
              style={styles.submitModalTitle}
            >
              Are you sure?
            </h2>

            <p style={styles.submitModalCopy}>
              Once you submit, your application will be sent to the Tribe team
              for review.
            </p>

            <div style={styles.submitModalActions}>
              <button
                type="button"
                onClick={closeSubmitConfirm}
                disabled={submitting}
                style={styles.submitModalCancelButton}
              >
                Go back
              </button>

              <button
                type="button"
                onClick={confirmSubmit}
                disabled={submitting}
                style={{
                  ...styles.submitModalConfirmButton,
                  ...(submitting
                    ? styles.continueButtonDisabled
                    : {}),
                }}
              >
                {submitting ? 'Submitting…' : 'Yes, submit'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ApplicationFooter />

    </main>
  );
}

function ResponsiveStyles() {
  return (
    <style>
      {`
        .referralFlowHeaderLeading { justify-self: start; min-width: 0; }
        .referralFlowHeaderTrailing { justify-self: end; min-width: 0; }
        .referralFlowLogo { justify-self: center; }
        .referralFlowBack { display: inline-flex; align-items: center; gap: 7px; min-height: 40px; padding: 0; border: 0; background: none; color: #62625d; font: inherit; font-size: 12px; cursor: pointer; }
        .referralFlowBack:hover { color: #171717; }
        .referralFlowBack:focus-visible, .referralFlowHeader button:focus-visible { outline: 2px solid #171717; outline-offset: 4px; }
        .referralFlowHeader.tcp-info-header { background: rgba(255,255,255,.94); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); }
        .referralApplicationLanding, .referralApplicationPage, .tcp-page { animation: creatorFlowEnter 160ms ease-out; }
        @keyframes creatorFlowEnter { from { opacity: 0; } to { opacity: 1; } }
        @media (max-width: 760px) {
          .referralFlowHeader { min-height: 56px !important; }
          .referralFlowLogo { width: 84px !important; }
          .referralFlowContext { display: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .referralApplicationLanding, .referralApplicationPage, .tcp-page { animation: none; }
        }
        .referralLogoBlack :is(path,circle,rect,polygon,polyline,ellipse) {
          fill: #050505 !important;
        }

        .referralLogoBlack [fill="none"] {
          fill: none !important;
        }

        .referralLogoBlack * {
          stroke: none !important;
        }

        .referralQuestionContent input:focus,
        .referralQuestionContent textarea:focus {
          box-shadow: none !important;
        }

        .referralQuestionContent div:focus-within {
          border-bottom-color: #171717;
        }

        .referralFormAmbient {
          pointer-events: none;
          user-select: none;
        }

        .referralFormAmbientCard {
          position: absolute;
          border: 1px solid rgba(17,17,17,.035);
          background: rgba(250,250,249,.72);
          box-shadow: 0 10px 28px rgba(0,0,0,.012);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }

        .referralFormAmbientCard strong,
        .referralFormAmbientCard small {
          display: block;
        }

        .referralFormAmbientCard strong {
          color: #777772;
          font-size: 9px;
          font-weight: 650;
        }

        .referralFormAmbientCard small {
          color: #a0a09b;
          font-size: 7px;
        }

        @media (max-width: 900px) {
          .referralFormAmbientProspect {
            left: 3% !important;
          }

          .referralFormAmbientQualified {
            right: 3% !important;
          }
        }

        @media (max-width: 680px) {
          .referralFormAmbientProspect,
          .referralFormAmbientQualified,
          .referralFormAmbientCode {
            opacity: .16 !important;
          }
        }

        @media (max-width: 520px) {
          .referralFormAmbientProspect,
          .referralFormAmbientQualified,
          .referralFormAmbientCode {
            display: none !important;
          }
        }

        .referralLandingAmbient {
          pointer-events: none;
          user-select: none;
        }

        .referralAmbient {
          position: absolute;
          border: 1px solid rgba(17,17,17,.045);
          background: rgba(250,250,249,.74);
          box-shadow: 0 10px 34px rgba(0,0,0,.018);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }

        .referralAmbient strong,
        .referralAmbient small {
          display: block;
        }

        .referralAmbient strong {
          color: #656560;
          font-size: 10px;
          font-weight: 650;
        }

        .referralAmbient small {
          color: #969691;
          font-size: 8px;
        }

        .referralAmbientMessage p {
          margin: 0;
          font-size: 9px;
          line-height: 1.4;
        }

        .referralAmbientEarnings > div > strong {
          color: #5f5f5a;
          font-size: 24px;
          line-height: 1;
          letter-spacing: -.04em;
        }

        .referralAmbientEarnings > div > i {
          width: 1px;
          height: 28px;
          background: #dfdfdb;
        }

        .referralAmbientEarnings > div > div {
          display: grid;
          gap: 2px;
        }

        .referralAmbientEarnings > div > div b {
          color: #666661;
          font-size: 15px;
          line-height: 1;
        }

        .referralAmbientEarnings > div > div span {
          color: #999994;
          font-size: 7px;
        }

        .referralAmbientCode > strong {
          margin-top: 6px;
          color: #686863;
          font-size: 14px;
          letter-spacing: .035em;
        }

        .referralAmbientCode > small {
          margin-top: 4px;
        }

        @media (max-width: 900px) {
          .referralAmbientProspect {
            left: 5% !important;
          }

          .referralAmbientMessage {
            right: 4% !important;
          }

          .referralAmbientQualified {
            right: 7% !important;
          }

          .referralAmbientEarnings {
            left: 7% !important;
          }
        }

        @media (max-width: 760px) {
          .referralApplicationLanding {
            padding: 24px 22px 18px !important;
          }

          .referralLandingHeader {
            min-height: 56px !important;
            padding: 0 !important;
            align-items: center !important;
          }

          .referralLandingHeader > div:last-child {
            font-size: 10px !important;
          }

          .referralLandingContent {
            width: calc(100% - 44px) !important;
          }

          .referralLandingAmbient {
            opacity: .48;
          }

          .referralAmbientProspect,
          .referralAmbientMessage,
          .referralAmbientQualified,
          .referralAmbientCode {
            display: none !important;
          }

          .referralAmbientEarnings {
            left: 50% !important;
            top: 16% !important;
            width: 250px !important;
            transform: translateX(-50%) !important;
          }

          .referralLandingFooter {
            min-height: 36px !important;
            padding: 0 !important;
            align-items: center !important;
          }
        }

        @media (max-width: 520px) {
          .referralLandingHeader > svg {
            width: 84px !important;
          }

          .referralLandingHeader > div:last-child {
            font-size: 9px !important;
            letter-spacing: .035em !important;
          }

          .referralLandingFooter > div:first-child {
            max-width: 180px;
            line-height: 1.35;
          }
        }

        @media (max-width: 760px) {
          .referralApplicationPage {
            padding: 24px 22px 18px !important;
          }

          .referralApplicationHeader {
            min-height: 56px !important;
            gap: 12px !important;
            padding: 0 !important;
            align-items: center !important;
          }

          .referralApplicationHeader > svg {
            width: 92px !important;
          }

          .referralApplicationHeader > div:last-child {
            font-size: 10px !important;
          }

          .referralApplicationFooter {
            min-height: 36px !important;
            padding: 0 !important;
            align-items: center !important;
          }
        }

        @media (max-width: 520px) {
          .referralApplicationHeader > svg {
            width: 84px !important;
          }

          .referralApplicationHeader > div:last-child {
            font-size: 9px !important;
            letter-spacing: .035em !important;
          }

          .referralApplicationFooter > div:first-child {
            max-width: 180px;
            line-height: 1.35;
          }
        }

        @media (max-width: 440px) {
          .referralEnterHint {
            display: none !important;
          }

          .referralHeader {
            height: auto !important;
          }

          .referralQuestionContent {
            width: 100% !important;
            padding: 7vh 20px 108px !important;
          }

          .referralQuestionContent h1 {
            max-width: 440px !important;
          }

          .referralDesktopActions {
            display: none !important;
          }

          .referralQuestionContent input,
          .referralQuestionContent textarea {
            max-width: 100% !important;
          }

          .referralNameGrid {
            grid-template-columns: 1fr !important;
          }

          .referralMobileControls {
            display: block !important;
            position: fixed !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            padding-bottom: env(safe-area-inset-bottom) !important;
          }
        }
      `}
    </style>
  );
}

function ApplicationFooter() {
  return (
    <footer
      className="referralApplicationFooter"
      style={styles.applicationFooter}
    >
      <div style={styles.applicationLegal}>
        © 2026 Tribe, Inc. All rights reserved.
      </div>

      <div style={styles.applicationSocials}>
        <a
          href="https://www.instagram.com/jointribe.io/"
          target="_blank"
          rel="noreferrer"
          aria-label="Instagram"
          style={styles.applicationSocialLink}
        >
          <FaInstagram />
        </a>

        <a
          href="https://www.linkedin.com/company/82302259"
          target="_blank"
          rel="noreferrer"
          aria-label="LinkedIn"
          style={styles.applicationSocialLink}
        >
          <FaLinkedin />
        </a>
      </div>
    </footer>
  );
}


function Header({ minimal = false }) {
  return (
    <header
      className="referralHeader referralApplicationHeader"
      style={styles.applicationHeader}
    >
      <TribeLogo
        className="referralLogoBlack"
        style={styles.applicationHeaderLogo}
        aria-label="Tribe"
      />

      <div style={styles.applicationHeaderTitle}>
        CREATOR APPLICATION
      </div>
    </header>
  );
}


function CreatorFlowHeader({ onBack, onApply, sticky = false }) {
  return (
    <header
      className={`referralFlowHeader${sticky ? ' tcp-info-header' : ''}`}
      style={{ ...styles.flowHeader, ...(sticky ? { position: 'sticky', top: 0 } : {}) }}
    >
      <div className="referralFlowHeaderLeading">
        {onBack ? (
          <button className="referralFlowBack" type="button" onClick={onBack}>
            <LuArrowLeft size={15} aria-hidden="true" />Back
          </button>
        ) : null}
      </div>
      <TribeLogo className="referralLogoBlack referralFlowLogo" style={styles.landingLogo} aria-label="Tribe" />
      <div className="referralFlowHeaderTrailing">
        {onApply ? (
          <button className="tcp-button tcp-header-apply" style={styles.flowHeaderButton} type="button" onClick={onApply}>
            Apply now <LuArrowRight size={15} aria-hidden="true" />
          </button>
        ) : (
          <span className="referralFlowContext" style={styles.applicationHeaderTitle}>CREATOR APPLICATION</span>
        )}
      </div>
    </header>
  );
}


function renderAnswer({
  step,
  form,
  inputRef,
  updateField,
  selectChoice,
}) {
  if (step.type === 'name') {
    return (
      <div className="referralNameGrid" style={styles.nameGrid}>
        <div>
          <label style={styles.fieldLabel}>First name</label>
          <input
            ref={inputRef}
            type="text"
            value={form.firstName}
            onChange={(e) => updateField('firstName', e.target.value)}
            placeholder="First name"
            autoComplete="given-name"
            style={styles.textInput}
          />
        </div>

        <div>
          <label style={styles.fieldLabel}>Last name</label>
          <input
            type="text"
            value={form.lastName}
            onChange={(e) => updateField('lastName', e.target.value)}
            placeholder="Last name"
            autoComplete="family-name"
            style={styles.textInput}
          />
        </div>
      </div>
    );
  }

  if (step.type === 'email') {
    return (
      <input
        ref={inputRef}
        type="email"
        value={form.email}
        onChange={(e) => updateField('email', e.target.value)}
        placeholder="name@example.com"
        autoComplete="email"
        style={styles.textInput}
      />
    );
  }

  if (step.type === 'birthday') {
    return (
      <div style={styles.iconInputWrap}>
        <LuCalendarDays
          aria-hidden="true"
          style={styles.inputLeadingIcon}
        />

        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="bday"
          value={form.birthday}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, 8);

            let formatted = digits;

            if (digits.length > 4) {
              formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
            } else if (digits.length > 2) {
              formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
            }

            updateField('birthday', formatted);
          }}
          placeholder="MM/DD/YYYY"
          maxLength={10}
          style={styles.iconTextInput}
        />
      </div>
    );
  }

  if (step.type === 'text') {
    if (step.id === 'city') {
      return (
        <div style={styles.iconInputWrap}>
          <LuMapPin
            aria-hidden="true"
            style={styles.inputLeadingIcon}
          />

          <input
            ref={inputRef}
            type="text"
            value={form.city}
            onChange={(e) =>
              updateField('city', e.target.value)
            }
            placeholder={step.placeholder}
            style={styles.iconTextInput}
          />
        </div>
      );
    }

    if (
      step.id === 'instagramHandle' ||
      step.id === 'tiktokHandle'
    ) {
      return (
        <div style={styles.prefixInputWrap}>
          <span
            aria-hidden="true"
            style={styles.inputPrefix}
          >
            @
          </span>

          <input
            ref={inputRef}
            type="text"
            value={String(form[step.id] || '').replace(/^@+/, '')}
            onChange={(e) =>
              updateField(
                step.id,
                e.target.value.replace(/^@+/, '')
              )
            }
            placeholder="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            style={styles.prefixTextInput}
          />
        </div>
      );
    }

    return (
      <input
        ref={inputRef}
        type="text"
        value={form[step.id]}
        onChange={(e) => updateField(step.id, e.target.value)}
        placeholder={step.placeholder}
        style={styles.textInput}
      />
    );
  }

  if (step.type === 'textarea') {
    return (
      <textarea
        ref={inputRef}
        value={form[step.id]}
        onChange={(e) => updateField(step.id, e.target.value)}
        placeholder={step.placeholder}
        rows={5}
        style={styles.textarea}
      />
    );
  }

  if (step.type === 'choice') {
    return (
      <div style={styles.choiceList}>
        {step.options.map((option, index) => {
          const selected = form[step.id] === option;

          return (
            <button
              key={option}
              type="button"
              onClick={() => selectChoice(option)}
              style={{
                ...styles.choiceButton,
                ...(selected ? styles.choiceButtonSelected : {}),
              }}
            >
              <span
                style={{
                  ...styles.choiceKey,
                  ...(selected ? styles.choiceKeySelected : {}),
                }}
              >
                {String.fromCharCode(65 + index)}
              </span>

              <span style={styles.choiceText}>{option}</span>

              {selected ? <span style={styles.check}>✓</span> : null}
            </button>
          );
        })}
      </div>
    );
  }

  return null;
}

const styles = {
  flowHeader: {
    position: 'relative',
    zIndex: 20,
    minHeight: 56,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    gap: 12,
    padding: 0,
    boxSizing: 'border-box',
  },
  flowHeaderButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    minHeight: 40,
    padding: '0 20px',
    border: '1px solid #151515',
    borderRadius: 999,
    background: '#151515',
    color: '#fff',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  landingPage: {
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden',
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    padding: '30px 44px 24px',
    background: CREATOR_FLOW_THEME.background,
    color: '#151515',
    boxSizing: 'border-box',
    fontFamily: CREATOR_FLOW_THEME.font,
  },

  landingHeader: {
    position: 'relative',
    zIndex: 6,
    minHeight: 56,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
  },

  landingLogo: {
    width: 92,
    height: 'auto',
    display: 'block',
  },

  landingHeaderTitle: {
    color: '#111',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '.055em',
    whiteSpace: 'nowrap',
  },

  landingAmbient: {
    position: 'absolute',
    zIndex: 1,
    inset: '88px 0 74px',
  },

  ambientProspect: {
    left: '13%',
    top: '22%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: 226,
    padding: '10px 12px',
    borderRadius: 14,
    color: '#6d6d68',
    opacity: .52,
  },

  ambientMessage: {
    right: '12%',
    top: '26%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: 236,
    padding: '11px 13px',
    borderRadius: 14,
    color: '#777772',
    opacity: .42,
  },

  ambientQualified: {
    right: '15%',
    bottom: '17%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: 226,
    padding: '10px 12px',
    borderRadius: 14,
    color: '#6d6d68',
    opacity: .48,
  },

  ambientEarnings: {
    left: '16%',
    bottom: '14%',
    width: 260,
    padding: '14px 15px',
    borderRadius: 16,
    color: '#666660',
    opacity: .34,
  },

  ambientCode: {
    right: '24%',
    top: '12%',
    width: 164,
    padding: '12px 14px',
    borderRadius: 13,
    color: '#777772',
    opacity: .30,
  },

  ambientAvatar: {
    width: 30,
    height: 30,
    flex: '0 0 30px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    background: '#e5e5e1',
    color: '#858580',
    fontSize: 9,
    fontWeight: 750,
  },

  ambientTextStack: {
    display: 'grid',
    gap: 3,
    minWidth: 0,
    flex: 1,
  },

  ambientStatus: {
    minWidth: 34,
    height: 17,
    display: 'grid',
    placeItems: 'center',
    padding: '0 7px',
    borderRadius: 999,
    background: '#ecece8',
    color: '#92928d',
    fontSize: 6,
    fontWeight: 800,
    letterSpacing: '.06em',
  },

  ambientDot: {
    width: 8,
    height: 8,
    flex: '0 0 8px',
    borderRadius: '50%',
    background: '#d8d8d4',
  },

  ambientCheck: {
    width: 28,
    height: 28,
    flex: '0 0 28px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    background: '#e5e5e1',
    color: '#7b7b76',
    fontSize: 10,
    fontWeight: 800,
  },

  ambientSmallLabel: {
    display: 'block',
    color: '#999994',
    fontSize: 7,
    fontWeight: 800,
    letterSpacing: '.08em',
  },

  ambientEarningsRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1px 1fr',
    alignItems: 'center',
    gap: 12,
    marginTop: 9,
  },

  landingContent: {
    position: 'relative',
    zIndex: 5,
    alignSelf: 'center',
    justifySelf: 'center',
    width: 'min(100%, 680px)',
    marginTop: -18,
    textAlign: 'center',
  },

  landingTitle: {
    margin: 0,
    color: '#171717',
    fontSize: 'clamp(32px, 3.4vw, 46px)',
    fontWeight: 720,
    lineHeight: 1.03,
    letterSpacing: '-.045em',
  },

  landingCopy: {
    maxWidth: 500,
    margin: '16px auto 0',
    color: '#62625d',
    fontSize: 'clamp(13px, 1.05vw, 15px)',
    lineHeight: 1.55,
  },

  landingActions: {
    marginTop: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    flexWrap: 'wrap',
  },

  landingButton: {
    minWidth: 180,
    minHeight: 50,
    padding: '0 26px',
    border: '1px solid #151515',
    borderRadius: 999,
    background: '#151515',
    color: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
  },

  landingEnterHint: {
    color: '#8a8a85',
    fontSize: 10,
  },

  landingFooter: {
    position: 'relative',
    zIndex: 6,
    minHeight: 36,
    marginTop: 'auto',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    color: '#a0a09b',
    boxSizing: 'border-box',
  },

  landingLegal: {
    fontSize: 9,
    fontWeight: 500,
  },

  landingSocials: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
  },

  landingSocialLink: {
    fontSize: '1.28rem',
    color: '#000',
    transition: 'color 0.2s ease',
    lineHeight: 1,
  },

  applicationFooter: {
    position: 'relative',
    zIndex: 8,
    minHeight: 36,
    marginTop: 'auto',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    color: '#a0a09b',
    boxSizing: 'border-box',
  },

  applicationLegal: {
    fontSize: 9,
    fontWeight: 500,
  },

  applicationSocials: {
    display: 'flex',
    alignItems: 'center',
    gap: 20,
  },

  applicationSocialLink: {
    fontSize: '1.28rem',
    color: '#000',
    transition: 'color 0.2s ease',
    lineHeight: 1,
  },

  formPage: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflowX: 'hidden',
    padding: '30px 44px 24px',
    background: CREATOR_FLOW_THEME.background,
    color: '#151515',
    boxSizing: 'border-box',
    fontFamily: CREATOR_FLOW_THEME.font,
  },

  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#ffff',
    color: '#151515',
    position: 'relative',
    boxSizing: 'border-box',
    fontFamily: CREATOR_FLOW_THEME.font,
  },

  header: {
    height: 64,
    padding: '0 26px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
  },

  logoSvg: {
    width: 82,
    height: 'auto',
    display: 'block',
  },

  applicationHeader: {
    position: 'relative',
    zIndex: 8,
    minHeight: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 24,
    padding: 0,
    boxSizing: 'border-box',
  },

  applicationHeaderLogo: {
    width: 92,
    height: 'auto',
    display: 'block',
    justifySelf: 'start',
  },

  applicationHeaderTitle: {
    justifySelf: 'end',
    color: '#111',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: '.055em',
    whiteSpace: 'nowrap',
  },









  hero: {
    flex: 1,
    width: 'min(650px, calc(100% - 40px))',
    margin: '0 auto',
    padding: '12vh 0 72px',
  },

  eyebrow: {
    marginBottom: 14,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: '0.11em',
  },

  heroTitle: {
    maxWidth: 650,
    margin: 0,
    fontSize: 'clamp(34px, 4.8vw, 52px)',
    lineHeight: 1.06,
    letterSpacing: '-0.045em',
    fontWeight: 650,
  },

  heroCopy: {
    maxWidth: 560,
    margin: '20px 0 0',
    fontSize: 'clamp(16px, 1.6vw, 18px)',
    lineHeight: 1.5,
    color: '#4d4d4d',
  },

  heroActions: {
    marginTop: 26,
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flexWrap: 'wrap',
  },

  formAmbient: {
    position: 'absolute',
    zIndex: 1,
    inset: '86px 0 58px',
    overflow: 'hidden',
  },

  formAmbientCard: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '10px 12px',
    borderRadius: 13,
    color: '#777772',
    opacity: .22,
  },

  formAmbientProspect: {
    left: '8%',
    top: '24%',
    width: 190,
  },

  formAmbientQualified: {
    right: '8%',
    bottom: '20%',
    width: 205,
  },

  formAmbientCode: {
    right: '13%',
    top: '18%',
    width: 150,
    display: 'grid',
    gap: 5,
    opacity: .16,
  },

  formAmbientAvatar: {
    width: 26,
    height: 26,
    flex: '0 0 26px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    background: '#e4e4e0',
    color: '#8b8b85',
    fontSize: 8,
    fontWeight: 700,
  },

  formAmbientCheck: {
    width: 26,
    height: 26,
    flex: '0 0 26px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    background: '#e4e4e0',
    color: '#83837e',
    fontSize: 9,
    fontWeight: 750,
  },

  formAmbientCopy: {
    display: 'grid',
    gap: 3,
    minWidth: 0,
  },

  formAmbientLabel: {
    color: '#999994',
    fontSize: 7,
    fontWeight: 800,
    letterSpacing: '.08em',
  },

  questionShell: {
    position: 'relative',
    zIndex: 3,
    flex: 1,
    width: '100%',
    display: 'grid',
    placeItems: 'center',
    boxSizing: 'border-box',
  },

  questionContent: {
    width: 'min(540px, calc(100% - 40px))',
    margin: '0 auto',
    padding: '7vh 0 118px',
    boxSizing: 'border-box',
    textAlign: 'left',
  },

  questionNumber: {
    width: '100%',
    margin: '0 0 10px',
    color: '#7d7d78',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '.02em',
    textAlign: 'left',
  },

  questionTitle: {
    maxWidth: 500,
    margin: 0,
    color: '#171717',
    fontSize: 'clamp(28px, 3vw, 38px)',
    lineHeight: 1.08,
    letterSpacing: '-.04em',
    fontWeight: 650,
    textAlign: 'left',
  },

  questionDescription: {
    maxWidth: 460,
    margin: '10px 0 0',
    color: '#73736e',
    fontSize: 12,
    lineHeight: 1.55,
    textAlign: 'left',
  },

  answerArea: {
    width: '100%',
    marginTop: 28,
    textAlign: 'left',
  },

  nameGrid: {
    width: 'min(100%, 460px)',
    margin: 0,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 18,
  },

  fieldLabel: {
    display: 'block',
    marginBottom: 7,
    color: '#686864',
    fontSize: 10,
    fontWeight: 650,
    textAlign: 'left',
  },

  textInput: {
    width: 'min(100%, 460px)',
    height: 50,
    display: 'block',
    margin: 0,
    padding: '0 4px',
    border: 0,
    borderBottom: '1.5px solid #a9a9a4',
    borderRadius: 0,
    background: 'transparent',
    color: '#171717',
    outline: 'none',
    boxSizing: 'border-box',
    fontSize: 18,
    fontWeight: 450,
    textAlign: 'left',
  },

  iconInputWrap: {
    width: 'min(100%, 460px)',
    height: 50,
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    margin: 0,
    borderBottom: '1.5px solid #a9a9a4',
    boxSizing: 'border-box',
  },

  inputLeadingIcon: {
    width: 17,
    height: 17,
    flex: '0 0 17px',
    color: '#777772',
    strokeWidth: 1.8,
  },

  iconTextInput: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    padding: 0,
    border: 0,
    outline: 'none',
    background: 'transparent',
    color: '#171717',
    fontFamily: 'inherit',
    fontSize: 18,
    fontWeight: 450,
    textAlign: 'left',
    boxSizing: 'border-box',
  },

  prefixInputWrap: {
    width: 'min(100%, 460px)',
    height: 50,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    margin: 0,
    borderBottom: '1.5px solid #a9a9a4',
    boxSizing: 'border-box',
  },

  inputPrefix: {
    flex: '0 0 auto',
    color: '#171717',
    fontSize: 18,
    fontWeight: 450,
    lineHeight: 1,
  },

  prefixTextInput: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    padding: 0,
    border: 0,
    outline: 'none',
    background: 'transparent',
    color: '#171717',
    fontFamily: 'inherit',
    fontSize: 18,
    fontWeight: 450,
    textAlign: 'left',
    boxSizing: 'border-box',
  },

  textarea: {
    width: 'min(100%, 460px)',
    minHeight: 124,
    display: 'block',
    margin: 0,
    padding: '12px 4px',
    border: 0,
    borderBottom: '1.5px solid #a9a9a4',
    borderRadius: 0,
    background: 'transparent',
    color: '#171717',
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    fontSize: 16,
    lineHeight: 1.5,
    textAlign: 'left',
  },

  choiceList: {
    width: 'min(100%, 460px)',
    margin: 0,
    display: 'grid',
    gap: 8,
  },

  choiceButton: {
    width: '100%',
    minHeight: 44,
    padding: '7px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    textAlign: 'left',
    border: '1px solid rgba(21,21,21,0.28)',
    borderRadius: 6,
    background: 'rgba(255,255,255,0.38)',
    color: '#151515',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background 120ms ease, border-color 120ms ease',
  },

  choiceButtonSelected: {
    border: '2px solid #151515',
    background: 'rgba(21,21,21,0.08)',
  },

  choiceKey: {
    width: 24,
    height: 24,
    flex: '0 0 24px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid rgba(21,21,21,0.3)',
    borderRadius: 4,
    background: '#fff',
    fontSize: 12,
    fontWeight: 850,
  },

  choiceKeySelected: {
    borderColor: '#151515',
    background: '#151515',
    color: '#fff',
  },

  choiceText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 1.35,
    fontWeight: 550,
  },

  check: {
    fontSize: 16,
    fontWeight: 900,
  },


  primaryButton: {
    minHeight: 40,
    padding: '0 16px',
    border: '1px solid #151515',
    borderRadius: 7,
    background: '#151515',
    color: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 800,
  },

  primaryButtonDisabled: {
    opacity: 0.35,
    cursor: 'default',
  },

  enterHint: {
    fontSize: 11,
    color: '#686868',
  },

  desktopActions: {
    marginTop: 30,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 10,
  },

  bottomControls: {
    display: 'none',
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    background: '#ffff',
  },

  bottomControlsInner: {
    width: 'min(650px, calc(100% - 40px))',
    minHeight: 68,
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },

  backButton: {
    minWidth: 118,
    height: 44,
    padding: '0 18px',
    border: '1px solid #d7d7d3',
    borderRadius: 999,
    background: 'rgba(255,255,255,.76)',
    color: '#555550',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 650,
  },

  continueButton: {
    minWidth: 152,
    height: 44,
    padding: '0 22px',
    border: '1px solid #151515',
    borderRadius: 999,
    background: '#151515',
    color: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 700,
  },

  continueButtonDisabled: {
    opacity: 0.35,
    cursor: 'default',
  },

  submitModalBackdrop: {
    position: 'fixed',
    zIndex: 1000,
    inset: 0,
    display: 'grid',
    placeItems: 'center',
    padding: 24,
    background: 'rgba(17,17,17,.18)',
    backdropFilter: 'blur(5px)',
    WebkitBackdropFilter: 'blur(5px)',
    boxSizing: 'border-box',
  },

  submitModal: {
    width: 'min(100%, 390px)',
    padding: '28px 28px 24px',
    border: '1px solid rgba(17,17,17,.08)',
    borderRadius: 22,
    background: '#fff',
    boxShadow: '0 24px 70px rgba(0,0,0,.12)',
    boxSizing: 'border-box',
  },

  submitModalEyebrow: {
    marginBottom: 12,
    color: '#8b8b86',
    fontSize: 9,
    fontWeight: 750,
    letterSpacing: '.08em',
  },

  submitModalTitle: {
    margin: 0,
    color: '#171717',
    fontSize: 28,
    fontWeight: 680,
    lineHeight: 1.05,
    letterSpacing: '-.035em',
  },

  submitModalCopy: {
    margin: '12px 0 0',
    color: '#6f6f6a',
    fontSize: 12,
    lineHeight: 1.55,
  },

  submitModalActions: {
    marginTop: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },

  submitModalCancelButton: {
    minWidth: 92,
    height: 40,
    padding: '0 16px',
    border: '1px solid #d7d7d3',
    borderRadius: 999,
    background: '#fff',
    color: '#555550',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 650,
  },

  submitModalConfirmButton: {
    minWidth: 118,
    height: 40,
    padding: '0 18px',
    border: '1px solid #151515',
    borderRadius: 999,
    background: '#151515',
    color: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 11,
    fontWeight: 700,
  },

  errorBox: {
    width: 'fit-content',
    maxWidth: 'min(100%, 360px)',
    margin: '16px 0 0',
    padding: '9px 11px',
    border: '1px solid #d5a7a7',
    borderRadius: 6,
    background: '#fff1f1',
    color: '#861f1f',
    fontSize: 14,
    fontWeight: 700,
  },


  successShell: {
    flex: 1,
    width: 'min(100%, 620px)',
    margin: '0 auto',
    display: 'grid',
    placeContent: 'center',
    justifyItems: 'center',
    padding: '48px 22px 120px',
    textAlign: 'center',
    boxSizing: 'border-box',
  },

  successMark: {
    width: 42,
    height: 42,
    marginBottom: 18,
    display: 'grid',
    placeItems: 'center',
    borderRadius: '50%',
    background: '#151515',
    color: '#fff',
    fontSize: 18,
    fontWeight: 800,
  },

  successTitle: {
    margin: 0,
    color: '#171717',
    fontSize: 'clamp(32px, 3.4vw, 46px)',
    fontWeight: 720,
    lineHeight: 1.03,
    letterSpacing: '-.045em',
  },

  successCopy: {
    maxWidth: 500,
    margin: '16px auto 0',
    color: '#62625d',
    fontSize: 'clamp(13px, 1.05vw, 15px)',
    lineHeight: 1.55,
  },

  successButton: {
    minWidth: 160,
    height: 46,
    marginTop: 24,
    padding: '0 22px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #151515',
    borderRadius: 999,
    background: '#151515',
    color: '#fff',
    textDecoration: 'none',
    boxSizing: 'border-box',
    fontSize: 12,
    fontWeight: 700,
  },
};

// Creator program information shown before the existing application form.


const CREATOR_PROGRAM_BONUSES = [
  { signups: 25, commission: 75, bonus: 100 },
  { signups: 50, commission: 150, bonus: 250 },
  { signups: 100, commission: 300, bonus: 600 },
  { signups: 200, commission: 600, bonus: 1200 },
];

const CREATOR_PROGRAM_RESPONSIBILITIES = [
  <React.Fragment>Create <strong>two distinct videos per week</strong> and post each on Instagram Reels. If you have a TikTok account, post each video there too.</React.Fragment>,
  <React.Fragment>Incorporate Tribe’s key talking points into each video.</React.Fragment>,
  <React.Fragment>Respond promptly to people requesting an invitation.</React.Fragment>,
  <React.Fragment>Invite people from your LA network to join.</React.Fragment>,
  <React.Fragment>Attend <strong>one Tribe event per month</strong>.</React.Fragment>,
];

const CREATOR_PROGRAM_VIDEO_IDEAS = [
  ["Get ready with me", "Introduce Tribe while doing your makeup or choosing an outfit."],
  ["Your upcoming plans", "Invite viewers to a Tribe event you’re attending."],
  ["Moving to LA", "Talk about making friends in a new city."],
  ["Trying something new", "Share something you’d like to try with Tribe."],
  ["Short clip with text", "Overlay the talking points on footage of yourself or your friends."],
];

const creatorFormatMoney = (amount) => `$${amount.toLocaleString("en-US")}`;

function CreatorProgramArrow() {
  return <LuArrowRight size={16} aria-hidden="true" />;
}

function CreatorProgramSection({ id, title, children }) {
  return (
    <section className={id === 'apply' ? 'tcp-apply' : 'tcp-section'} id={`tcp-${id}`} aria-labelledby={`tcp-${id}-title`}>
      <h2 id={`tcp-${id}-title`}>{title}</h2>
      <div className="tcp-section-body">{children}</div>
    </section>
  );
}

function CreatorProgramIntro({ onApply, onBack }) {

  return (
    <div className="tcp-page">
      <ResponsiveStyles />
      <style>{CREATOR_PROGRAM_STYLES}</style>
      <a className="tcp-skip" href="#tcp-main">Skip to program details</a>

      <CreatorFlowHeader onBack={onBack} onApply={onApply} sticky />

      <main id="tcp-main" className="tcp-main" tabIndex={-1}>
        <div className="tcp-intro">
          <h1>Tribe Creator Program</h1>
        </div>

        <div className="tcp-sections">
          <CreatorProgramSection id="about" title="About Tribe">
            <p>Tribe is a free, invite-only social club designed to help people make friends through curated, host-led experiences in Los Angeles.</p>
            <details className="tcp-how">
              <summary><LuChevronDown className="tcp-chevron" size={16} aria-hidden="true" />How it works</summary>
            <ol className="tcp-steps">
              <li><strong>Sign up:</strong> Use an invite code or apply at <a href="https://www.jointribe.io">jointribe.io</a>.</li>
              <li><strong>Browse and reserve:</strong> Choose an experience and reserve a spot in the Tribe web app.</li>
              <li><strong>Show up to the gathering:</strong> Your host guides the activities and helps everyone connect.</li>
              <li><strong>Match afterward:</strong> Match with people you met and keep chatting in the web app.</li>
            </ol>
            </details>
          </CreatorProgramSection>

          <CreatorProgramSection id="overview" title="Program overview">
            <p>Your goal is to bring new members into Tribe through videos, personal invitations, and following up with interested people.</p>
            <p><strong>You’ll earn money for each qualifying video you post and each new signup or application credited to you.</strong></p>
          </CreatorProgramSection>

          <CreatorProgramSection id="responsibilities" title="Responsibilities">
            <ul className="tcp-requirements">
              {CREATOR_PROGRAM_RESPONSIBILITIES.map((item, index) => <li key={index}><LuCheck size={15} aria-hidden="true" /><span>{item}</span></li>)}
            </ul>
          </CreatorProgramSection>

          <CreatorProgramSection id="compensation" title="Compensation">
            <div className="tcp-pay-rates">
              <div>
                <h3><span className="tcp-rate">$10</span> per video</h3>
                <p>Earn $10 per approved video posted on Instagram Reels and, if you have a TikTok account, TikTok. Cross-posting counts as one paid video.</p>
              </div>
              <div>
                <h3><span className="tcp-rate">$3</span> per signup</h3>
                <p>Members must be credited to you through your code or the application’s referral selection.</p>
              </div>
            </div>
            <h3>Monthly signup bonuses</h3>
            <div className="tcp-table-wrap">
              <table>
                <caption className="tcp-sr-only">Monthly signups, commission, bonus, and total, excluding video payments</caption>
                <thead>
                  <tr><th scope="col">Signups</th><th scope="col">Commission</th><th scope="col">Bonus</th><th scope="col">Total</th></tr>
                </thead>
                <tbody>
                  {CREATOR_PROGRAM_BONUSES.map(({ signups, commission, bonus }) => (
                    <tr key={signups}>
                      <th scope="row">{signups}</th>
                      <td>{creatorFormatMoney(commission)}</td><td>{creatorFormatMoney(bonus)}</td><td><strong>{creatorFormatMoney(commission + bonus)}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="tcp-table-note">Only the highest bonus tier applies each calendar month. Video payments are additional.</p>
            <p className="tcp-earnings-example"><strong>Example:</strong> Eight qualifying videos and 50 signups in one calendar month earn <strong>$480 total</strong>.</p>
            <details className="tcp-supporting">
              <summary><LuChevronDown className="tcp-chevron" size={16} aria-hidden="true" />What counts as a signup?</summary>
            <p>A referral counts when a new, unique person in the LA area creates an account or successfully submits a membership application and credits you through your invite code or the application’s referral selection. Each person counts once, even if they apply and later create an account. Existing members, duplicates, and self-referrals don’t count.</p>
            </details>
            <p>Video payments are sent after you email the live post links to <a href="mailto:nati@jointribe.io">nati@jointribe.io</a>. Referral commissions and bonuses are paid <strong>on the 1st</strong> for the previous month.</p>
          </CreatorProgramSection>

          <CreatorProgramSection id="video-guidance" title="Video guidance">
            <p>Each video should take about 5–10 minutes to make. Simple talking-head videos or short clips with text are perfect—no elaborate filming or editing needed.</p>
            <p>In your own words, include these two points in every video:</p>
            <ul className="tcp-list">
              <li><strong>What Tribe is:</strong> Explain what viewers would be joining and how it helps them make friends.</li>
              <li><strong>How to join:</strong> Invite viewers to DM you for an invite or apply at <a href="https://www.jointribe.io">jointribe.io</a>.</li>
            </ul>
            <p className="tcp-note">Keep your invite code private. Send it and your signup link directly to anyone requesting an invitation.</p>
            <details className="tcp-supporting">
              <summary><LuChevronDown className="tcp-chevron" size={16} aria-hidden="true" />Example wording and video ideas</summary>
              <div className="tcp-talking-point">
                <h4>What Tribe is</h4>
                <blockquote>“Tribe is a free, invite-only social club designed to help people make friends through curated, host-led experiences in Los Angeles.”</blockquote>
              </div>
              <div className="tcp-talking-point">
                <h4>How to join</h4>
                <blockquote>“They’re letting me invite my followers to join! DM me for an invite, or apply at jointribe.io.”</blockquote>
              </div>
              <ul className="tcp-list tcp-ideas">
                {CREATOR_PROGRAM_VIDEO_IDEAS.map(([title, description]) => <li key={title}><strong>{title}:</strong> {description}</li>)}
              </ul>
              <p>Use your own style, as long as you include both talking points.</p>
            </details>
          </CreatorProgramSection>

        </div>

          <CreatorProgramSection id="apply" title="How to get started">
            <p>Interested in becoming a Tribe creator? Apply below.</p>
            <button className="tcp-button" type="button" onClick={onApply}>Apply now <CreatorProgramArrow /></button>
          </CreatorProgramSection>
      </main>
      <ApplicationFooter />
      <div className="tcp-mobile-apply">
        <button className="tcp-button" type="button" onClick={onApply}>Apply now <CreatorProgramArrow /></button>
      </div>
    </div>
  );
}

const CREATOR_PROGRAM_STYLES = `
.tcp-page {
  min-height: 100vh;
  padding: 30px 44px 24px;
  box-sizing: border-box;
  background: ${CREATOR_FLOW_THEME.background};
  background-attachment: fixed;
  color: ${CREATOR_FLOW_THEME.ink};
  font-family: ${CREATOR_FLOW_THEME.font};
  font-size: 14px;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}
.tcp-page *, .tcp-page *::before, .tcp-page *::after { box-sizing: border-box; }
.tcp-page a { color: inherit; text-underline-offset: 3px; }
.tcp-page a:focus-visible, .tcp-page button:focus-visible, .tcp-page summary:focus-visible { outline: 2px solid #171717; outline-offset: 4px; }
.tcp-page .tcp-skip { position: fixed; z-index: 100; left: 16px; top: 12px; transform: translateY(-200%); padding: 10px 16px; border-radius: 6px; background: #151515; color: #fff; }
.tcp-page .tcp-skip:focus { transform: none; }
.tcp-page .tcp-main { width: min(100%, 640px); margin: 0 auto; padding: 64px 0 56px; outline: none; }
.tcp-page .tcp-intro { text-align: center; margin-bottom: 48px; }
.tcp-page h1 { margin: 0; color: #171717; font-size: clamp(32px, 3.4vw, 46px); font-weight: 720; line-height: 1.03; letter-spacing: -.045em; }
.tcp-page .tcp-section { padding: 0 0 30px; margin: 0 0 30px; border-bottom: 1px solid ${CREATOR_FLOW_THEME.border}; }
.tcp-page .tcp-section:last-child { margin-bottom: 0; }
.tcp-page h2 { margin: 0 0 14px; color: #171717; font-size: 18px; font-weight: 650; line-height: 1.3; letter-spacing: -.025em; }
.tcp-page h3 { margin: 22px 0 10px; color: #171717; font-size: 14px; font-weight: 650; line-height: 1.4; }
.tcp-page h4 { margin: 0 0 8px; color: #171717; font-size: 14px; font-weight: 650; }
.tcp-page .tcp-section-body { color: ${CREATOR_FLOW_THEME.muted}; }
.tcp-page p { margin: 0 0 14px; }
.tcp-page p:last-child { margin-bottom: 0; }
.tcp-page strong { color: #252523; font-weight: 650; }
.tcp-page .tcp-list, .tcp-page .tcp-steps { margin: 10px 0 16px; padding-left: 20px; }
.tcp-page li { padding-left: 3px; margin-bottom: 10px; }
.tcp-page li:last-child { margin-bottom: 0; }
.tcp-page .tcp-list:last-child { margin-bottom: 0; }
.tcp-page .tcp-requirements { list-style: none; padding: 0; margin: 0; display: grid; gap: 13px; }
.tcp-page .tcp-requirements li { display: grid; grid-template-columns: 15px minmax(0, 1fr); align-items: start; gap: 11px; padding: 0; margin: 0; }
.tcp-page .tcp-requirements svg { margin-top: 4px; color: #666660; stroke-width: 1.7; }
.tcp-page .tcp-how, .tcp-page .tcp-supporting { margin: 18px 0; border: 1px solid ${CREATOR_FLOW_THEME.border}; border-radius: 6px; background: rgba(255,255,255,.45); }
.tcp-page .tcp-how:last-child, .tcp-page .tcp-supporting:last-child { margin-bottom: 0; }
.tcp-page summary::-webkit-details-marker { display: none; }
.tcp-page summary::marker { content: ''; }
.tcp-page .tcp-how > summary, .tcp-page .tcp-supporting > summary { display: flex; flex-direction: row-reverse; align-items: center; justify-content: space-between; gap: 16px; min-height: 44px; padding: 11px 14px; list-style: none; color: #555550; font-size: 13px; font-weight: 550; cursor: pointer; }
.tcp-page summary:hover { color: #171717; background: rgba(255,255,255,.5); }
.tcp-page .tcp-chevron { flex: 0 0 auto; color: #85857f; transition: transform 160ms ease; }
.tcp-page details[open] > summary > .tcp-chevron { transform: rotate(180deg); }
.tcp-page .tcp-how .tcp-steps { margin: 4px 14px 16px 18px; padding-left: 18px; }
.tcp-page .tcp-supporting > p, .tcp-page .tcp-supporting > .tcp-talking-point { margin: 4px 14px 16px; }
.tcp-page .tcp-supporting > ul { margin: 4px 14px 16px; padding-left: 18px; }
.tcp-page blockquote { margin: 0; padding: 10px 13px; border-left: 2px solid #d5d5d0; background: rgba(246,246,244,.8); color: #62625d; font-size: 13px; line-height: 1.6; }
.tcp-page .tcp-note, .tcp-page .tcp-table-note { color: #777771; font-size: 12px; line-height: 1.55; }
.tcp-page .tcp-pay-rates { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 24px; }
.tcp-page .tcp-pay-rates > div { padding: 18px; border: 1px solid ${CREATOR_FLOW_THEME.border}; border-radius: 6px; background: rgba(255,255,255,.45); }
.tcp-page .tcp-pay-rates h3 { margin: 0 0 10px; color: #62625d; font-size: 12px; font-weight: 550; }
.tcp-page .tcp-rate { display: block; color: #171717; font-size: 30px; font-weight: 650; letter-spacing: -.04em; line-height: 1.1; margin-bottom: 6px; }
.tcp-page .tcp-pay-rates p { font-size: 12px; line-height: 1.55; }
.tcp-page .tcp-table-wrap { width: 100%; overflow-x: auto; margin: 14px 0 12px; border: 1px solid ${CREATOR_FLOW_THEME.border}; border-radius: 6px; background: rgba(255,255,255,.45); }
.tcp-page table { width: 100%; border-collapse: collapse; font-size: 13px; font-variant-numeric: tabular-nums; }
.tcp-page th, .tcp-page td { padding: 12px 14px; text-align: right; white-space: nowrap; }
.tcp-page th:first-child { text-align: left; }
.tcp-page thead th { color: #777771; font-size: 11px; font-weight: 550; background: rgba(246,246,244,.8); }
.tcp-page tbody th { font-weight: 500; }
.tcp-page tbody tr + tr { border-top: 1px solid rgba(21,21,21,.07); }
.tcp-page .tcp-earnings-example { margin: 18px 0; }
.tcp-page .tcp-apply { padding-top: 30px; }
.tcp-page .tcp-apply p { color: #62625d; }
.tcp-page .tcp-button { display: inline-flex; align-items: center; justify-content: center; gap: 12px; min-width: 180px; min-height: 50px; padding: 0 26px; border: 1px solid #151515; border-radius: 999px; background: #151515; color: #fff; font-family: inherit; font-size: 13px; font-weight: 700; cursor: pointer; }
.tcp-page .tcp-button:hover { background: #333; border-color: #333; }
.tcp-page .tcp-info-header .tcp-header-apply { min-width: 0; min-height: 40px; }
.tcp-page .tcp-mobile-apply { display: none; }
.tcp-page .tcp-sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
@media (max-width: 760px) {
  .tcp-page { padding: 24px 22px calc(100px + env(safe-area-inset-bottom, 0px)); }
  .tcp-page .tcp-main { padding: 42px 0 36px; }
  .tcp-page .tcp-intro { margin-bottom: 36px; }
  .tcp-page .tcp-info-header .tcp-header-apply { display: none; }
  .tcp-page .tcp-apply { display: none; }
  .tcp-page .tcp-mobile-apply { display: block; position: fixed; bottom: 0; left: 0; right: 0; z-index: 30; padding: 14px 22px calc(14px + env(safe-area-inset-bottom, 0px)); border-top: 1px solid rgba(21,21,21,.1); background: rgba(255,255,255,.96); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); }
  .tcp-page .tcp-mobile-apply .tcp-button { display: flex; width: 100%; }
}
@media (max-width: 480px) {
  .tcp-page .tcp-section { margin-bottom: 26px; padding-bottom: 26px; }
  .tcp-page h2 { font-size: 17px; }
  .tcp-page .tcp-pay-rates > div { padding: 14px; }
  .tcp-page .tcp-pay-rates p { font-size: 12px; }
  .tcp-page th, .tcp-page td { padding: 10px 9px; }
}
@media (max-width: 360px) {
  .tcp-page .tcp-pay-rates { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) {
  .tcp-page .tcp-chevron { transition: none; }
}
`;
